import React from 'react';

import {
  Button,
  Flexbox,
  WithPopover,
  FontAwesomeIcon,
  Text,
} from '@schoolinks/ui-components';
import cn from 'classnames';
import { isEqual, get, isEmpty } from 'lodash';
import { shape, arrayOf, func, string, bool } from 'prop-types';
import { connect } from 'react-redux';

import { NavLink } from 'react-router-dom';

import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faCircleQuestion';
import { faHouse } from '@fortawesome/pro-regular-svg-icons/faHouse';
import { faLockKeyhole } from '@fortawesome/pro-regular-svg-icons/faLockKeyhole';
import { faHouse as faHouseSolid } from '@fortawesome/pro-solid-svg-icons/faHouse';

import BetaChip from 'components/Common/Chips/Beta';
import HomeItem from 'components/Common/Layout/components/HomeItem';
import SideNavWrapper from 'components/Common/Layout/components/SideNavWrapper';
import { MENU_ICON_MAP_ENH } from 'components/Common/LayoutV2/constants';
import {
  getRelevantNavItems,
  getHiddenNavItem,
  isElementaryPage,
  getFormattedRoute,
  getIsMenuActive,
} from 'components/Common/LayoutV2/utils';
import withRouter from 'containers/withRouter';
import withUserTypeAndOrgInfo from 'containers/withUserTypeAndOrgInfo';

import useModal from 'hooks/useModal';

import { RouterPropTypes } from 'propTypes/RouterPropTypes';
import { ensureArray } from 'utils/arrays';
import { conditionalObject } from 'utils/objects';

import s from './DesktopSideNav.css';

const NavOptionText = ({ text, isEnabled, upsellModalData, beta, onClose }) => {
  const { showModal, hideModal } = useModal();

  const textContent = (
    <Flexbox alignItems="center" gap={1} width="100%">
      {!isEnabled && (
        <FontAwesomeIcon color="disabledDefault" icon={faLockKeyhole} />
      )}
      <Text
        className={s.textContent}
        color={isEnabled ? 'default' : 'disabledDefault'}
        noWrap
        variant="bodySm500"
      >
        {text}
      </Text>
    </Flexbox>
  );

  if (isEnabled) {
    return (
      <>
        {beta ? (
          <>
            {textContent} <BetaChip className={s.betaChip} />
          </>
        ) : (
          textContent
        )}
      </>
    );
  }

  return (
    <Flexbox
      justifyContent="space-between"
      width="100%"
      alignItems="center"
      gap={2}
    >
      {textContent}
      {upsellModalData && (
        <Button
          className={s.upsellButton}
          hasIconRight
          size="small"
          icon={<FontAwesomeIcon icon={faCircleQuestion} />}
          label="Learn more"
          onClick={() => {
            onClose();
            showModal('FEATURE_UPSELL_MODAL', {
              data: upsellModalData,
              onClose: hideModal,
            });
          }}
        />
      )}
    </Flexbox>
  );
};

NavOptionText.propTypes = {
  text: string.isRequired,
  isEnabled: bool.isRequired,
  upsellModalData: shape({}),
  beta: bool,
  onClose: func.isRequired,
};

NavOptionText.defaultProps = {
  upsellModalData: null,
  beta: false,
};

@withRouter
@withUserTypeAndOrgInfo
@connect(
  ({ user: { layout = {}, type = [], currentUserMode } = {} }) => ({
    sideNav: getRelevantNavItems(layout, type, 'sideNav', currentUserMode),
  }),
  null,
)
export default class DesktopSideNav extends React.Component {
  shouldComponentUpdate(nextProps, nextStates) {
    return !isEqual(nextProps, this.props) || !isEqual(nextStates, this.state);
  }

  render() {
    const { sideNav = [], isOnboarding } = this.props;
    const {
      router: {
        location: { pathname },
        match,
      },
      user,
      ...restProps
    } = this.props;
    const baseNav = sideNav?.find(el => el.type === 'base');

    const isElementary = isElementaryPage(pathname);
    const MENU_ICON_MAP = MENU_ICON_MAP_ENH({ isElementary, user });

    const isHomeActive = [
      '/dashboard',
      '/newsfeed',
      '/el/dashboard',
      '/el/student/dashboard',
    ].some(route => pathname.startsWith(route));

    return (
      <SideNavWrapper {...restProps}>
        <HomeItem isOnboarding={isOnboarding} />
        <Flexbox
          alignItems="center"
          flexDirection="column"
          className={cn(s.menuButtonContainer, {
            [s.active]: isHomeActive,
          })}
          component={NavLink}
          to="/"
        >
          <FontAwesomeIcon
            icon={isHomeActive ? faHouseSolid : faHouse}
            size="xl"
          />
          <Text color="default" textAlign="center" variant="captionReg700">
            Home
          </Text>
        </Flexbox>
        {baseNav &&
          baseNav.items?.map(menu => {
            const menuContext = get(MENU_ICON_MAP, menu.key);
            const MenuIcon = menuContext?.icon;
            const orgUuid = baseNav?.orgUuid;

            const isMenuItemHidden = getHiddenNavItem({ menu });

            if (isMenuItemHidden) {
              return null;
            }

            const isMenuActive = getIsMenuActive({
              menuContext,
              pathname,
              orgUuid,
            });

            const isMenuActiveSpecialCondition = get(
              menuContext,
              'activeCondition',
              () => null,
            )(match, pathname, isMenuActive);

            if (!MenuIcon) {
              console.warn(
                `${menu.key} does not have mapping to any ICON in MENU_ICON_MAP, please add one in order for it to show up on the side nav`,
              );
              return null;
            }

            const isActive =
              isMenuActiveSpecialCondition !== null
                ? isMenuActiveSpecialCondition
                : isMenuActive;

            return (
              <WithPopover
                key={menu.key}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                paperClass={s.menuContainer}
                menuProps={{
                  items: ensureArray(menu?.menuOptions).map(opt => {
                    const {
                      key,
                      isEnabled,
                      tooltip,
                      name,
                      upsellModalData = null,
                      beta,
                    } = opt;

                    const link = getFormattedRoute({
                      orgUuid,
                      navItem: opt,
                      user,
                    });

                    if (isEmpty(link)) {
                      console.warn(
                        `${key} does not have mapping to any link in NAV_LINK_MAP, please add one in order for it to show up on the side nav`,
                      );

                      return {};
                    }

                    return {
                      href: link,
                      disabled: !isEnabled,
                      ...conditionalObject(!!tooltip, {
                        tooltipProps: {
                          title: tooltip,
                        },
                      }),
                      iconRight: upsellModalData ? false : undefined,
                      textContent: ({ onClose }) => (
                        <NavOptionText
                          text={name}
                          isEnabled={isEnabled}
                          upsellModalData={upsellModalData}
                          beta={beta}
                          onClose={onClose}
                        />
                      ),
                      id: key,
                    };
                  }),
                }}
                anchorButtonClass={s.menuButton}
                anchorContent={() => (
                  <Flexbox
                    alignItems="center"
                    flexDirection="column"
                    className={cn(s.menuButtonContainer, {
                      [s.active]: isActive,
                    })}
                  >
                    <FontAwesomeIcon
                      icon={isActive ? MenuIcon.solid : MenuIcon.regular}
                      size="xl"
                    />
                    <Text
                      color="default"
                      textAlign="center"
                      variant="captionReg700"
                    >
                      {menu.name}
                    </Text>
                  </Flexbox>
                )}
              />
            );
          })}
      </SideNavWrapper>
    );
  }
}

DesktopSideNav.propTypes = {
  sideNav: arrayOf(shape({})),
  user: shape({}),
  router: RouterPropTypes,
  isOnboarding: bool,
};

DesktopSideNav.defaultProps = {
  sideNav: [],
  user: {},
  router: undefined,
  isOnboarding: false,
};
